import { format } from 'date-fns';
import { GameDto } from '../../models/game/dto/game.dto';
import { EventDto } from '../../models/event/dto/event.dto';
import { CoachingSessionDto } from '../../models/coach/dto/coaching-session.dto';
import { CalendarEventType } from '../court-details/CourtDetailsPage';

export type CalendarEventForExport = {
    eventType: CalendarEventType;
    title: string;
    start: Date;
    end: Date;
    resourceId: number;
    game?: GameDto;
    coachingSession?: CoachingSessionDto;
    event?: EventDto;
};

export type ExportTranslate = (key: string) => string;

function escapeCsvCell(value: string | number | undefined | null): string {
    if (value === undefined || value === null) return '';
    const s = String(value);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}

function formatDateTime(d: Date): string {
    return format(d, 'yyyy-MM-dd HH:mm');
}

function fullName(firstName?: string, lastName?: string): string {
    return [firstName, lastName].filter(Boolean).join(' ').trim() || '';
}

function getParticipantsLabel(e: CalendarEventForExport): string {
    if (e.game) {
        const organiserName = fullName(e.game.organiser?.firstName, e.game.organiser?.lastName);
        const participantNames = (e.game.participants || [])
            .map((p) => fullName(p.participant?.firstName, p.participant?.lastName))
            .filter(Boolean);
        const all = organiserName ? [organiserName, ...participantNames] : participantNames;
        return [...new Set(all)].join('; ');
    }
    if (e.coachingSession?.user) {
        return fullName(e.coachingSession.user.firstName, e.coachingSession.user.lastName);
    }
    if (e.event) {
        if (e.event.teamBased && e.event.teams?.length) {
            const names = e.event.teams.flatMap((t) =>
                (t.participants || []).map((p) =>
                    fullName(p.participant?.firstName, p.participant?.lastName)
                )
            );
            return [...new Set(names)].filter(Boolean).join('; ');
        }
        const names = (e.event.participants || []).map((p) =>
            fullName(p.participant?.firstName, p.participant?.lastName)
        );
        return names.filter(Boolean).join('; ');
    }
    return '';
}

/**
 * Builds CSV content for calendar events (games, coaching sessions, blockades, events).
 * Availabilities are not included — pass only the main events array, not backgroundEvents.
 */
export function buildCalendarCsv(
    events: CalendarEventForExport[],
    getCourtName: (courtId: number) => string,
    translate: ExportTranslate
): string {
    const t = (k: string) => translate(k) || k;
    const headers = [
        t('calendar.export_col_type'),
        t('calendar.export_col_court'),
        t('calendar.export_col_start'),
        t('calendar.export_col_end'),
        t('calendar.export_col_title'),
        t('calendar.export_col_participants'),
        t('calendar.export_col_status'),
    ];
    const canceledLabel = t('calendar.export_status_canceled');
    const typeLabel = (eventType: CalendarEventType) => {
        switch (eventType) {
            case CalendarEventType.Game:
                return t('calendar.export_type_game');
            case CalendarEventType.CoachingSession:
                return t('calendar.export_type_coaching');
            case CalendarEventType.Blockade:
                return t('calendar.export_type_blockade');
            case CalendarEventType.Event:
                return t('calendar.export_type_event');
            default:
                return String(eventType);
        }
    };

    const headerRow = headers.map(escapeCsvCell).join(',');
    const rows = events.map((e) => {
        const status = e.title.startsWith('[CANCELED]') ? canceledLabel : '';
        const participants = getParticipantsLabel(e);
        const row = [
            typeLabel(e.eventType),
            getCourtName(e.resourceId),
            formatDateTime(e.start),
            formatDateTime(e.end),
            e.title,
            participants,
            status,
        ];
        return row.map(escapeCsvCell).join(',');
    });
    return [headerRow, ...rows].join('\r\n');
}

/**
 * Triggers a file download of the given CSV content.
 */
export function downloadCsv(csvContent: string, filename: string): void {
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
    a.click();
    URL.revokeObjectURL(url);
}
